import type { LucideIcon } from "lucide-react";
import type { OrganizationMemberRoleName } from "@multi-chat/shared";
import { cn } from "../../lib/utils.js";
import {
  organizationAccessKind,
  organizationIconForRole,
  type OrganizationAccessKind,
} from "../../features/organizations/OrganizationIcon.js";

const BADGE_MINT = "text-[#7fe0c0]";

const MEMBERSHIP_BADGE_TONE: Record<OrganizationAccessKind, string> = {
  owner: BADGE_MINT,
  member: "text-[#9ec9bb]",
  platform: BADGE_MINT,
};

/**
 * `rail` sits on the dark sidebar; `card` sits on the larger avatar of the light
 * account identity card, so its cut-out border matches that card.
 */
export type AvatarBadgeSize = "rail" | "card";

const BADGE_SIZE: Record<AvatarBadgeSize, { className: string; iconSize: number }> = {
  rail: { className: "-end-1 -bottom-1 size-[17px] border-[#121e1f]", iconSize: 9 },
  card: { className: "-end-1.5 -bottom-1.5 size-[21px] border-[#eff8f4]", iconSize: 11 },
};

/** Context mark on the bottom-end corner of an avatar; the avatar must be `relative`. */
export function RailAvatarBadge({
  icon: Icon,
  tone = BADGE_MINT,
  access,
  size = "rail",
}: {
  icon: LucideIcon;
  tone?: string;
  access?: OrganizationAccessKind | "super-admin";
  size?: AvatarBadgeSize;
}): React.JSX.Element {
  const { className, iconSize } = BADGE_SIZE[size];
  return (
    <span
      className={cn(
        "absolute inline-grid place-items-center rounded-full border-2 bg-[#1c2b2b]",
        className,
        tone,
      )}
      aria-hidden="true"
      data-testid="rail-avatar-badge"
      data-access={access}
      data-size={size}
    >
      <Icon size={iconSize} strokeWidth={2.6} aria-hidden="true" />
    </span>
  );
}

/** `role` is the real membership role; null means Platform Admin access without a membership. */
export function MembershipAvatarBadge({
  role,
  size,
}: {
  role: OrganizationMemberRoleName | null;
  size?: AvatarBadgeSize;
}): React.JSX.Element {
  const access = organizationAccessKind(role);
  return (
    <RailAvatarBadge
      icon={organizationIconForRole(role)}
      tone={MEMBERSHIP_BADGE_TONE[access]}
      access={access}
      size={size}
    />
  );
}
